import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Grid, makeStyles } from "@material-ui/core";
import TextFieldGroup from "../../../reusableComponents/TextFieldGroup";
import ButtonField from "../../../reusableComponents/ButtonField";
import SelectSingleValues from "../../../reusableComponents/SelectSingleValues";
import { editVideo, deleteVideo } from "../../../redux/actions/CoursesActions";
import isEmpty from "../../../reusableComponents/is-empty";
import SelectMultipleValues from "../../../reusableComponents/SelectMultipleValues";
import ReactPlayer from "react-player";
import { getVimeoDuration, isPlayableEmbed, normalizeEmbedUrlForSave } from "../../../reusableComponents/videoUrlUtils";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "fit-content",
    marginTop: theme.spacing(1),
    maxWidth: "500px",
  },
  buttonContainer: {
    margin: theme.spacing(1, 0),
    display: "flex",
    justifyContent: "flex-start",
  },
  singleSelect: {
    margin: theme.spacing(1, 0),
  },
  prerequisite: {
    margin: theme.spacing(2, 0),
  },
  formSubmit: {
    textTransform: "capitalize",
    fontWeight: 600,
    letterSpacing: 1,
    background: "#3d4977",
    color: "#F8F9F9",
    "&:hover": {
      background: "#1e293a",
    },
  },
}));

const VideoEdit = ({
  editVideo,
  deleteVideo,
  auth,
  videoData,
  courseId,
  setEditPopup,
  videoOrders,
}) => {
  const classes = useStyles();
  const [videoOrderList, setVideoOrderList] = useState([]);
  const [duration, setDuration] = useState();
  const [saving, setSaving] = useState(false);
  // embedUrlRef always holds the latest typed/pasted value — bypasses formik async state
  const embedUrlRef = useRef(videoData?.embedUrl || "");
  const lastInitializedVideoId = useRef(null);
  const previewList = [
    { title: "Yes", value: true },
    { title: "No", value: false },
  ];
  const visibility = [
    { title: "Hide", value: false },
    { title: "Unhide", value: true },
  ];

  const formik = useFormik({
    initialValues: {
      orderId: "",
      title: "",
      description: "",
      embedUrl: "",
      prerequisiteVideos: [],
      preview: "No",
      selectedOrder: {},
      visibility: {},
    },
    validationSchema: Yup.object({
      orderId: Yup.string().trim(),
      title: Yup.string().trim().required("Video Title is required field"),
      embedUrl: Yup.string().trim().required("Video URL is required"),
      description: Yup.string()
        .trim()
        .required("Description is required field"),
    }),
  });

  // Set form from video only when opening for this video (don't overwrite user's pasted URL)
  useEffect(() => {
    if (!videoData || !videoData._id) return;
    if (lastInitializedVideoId.current === videoData._id) return;
    lastInitializedVideoId.current = videoData._id;

    formik.setFieldValue("orderId", videoData?.orderId?.toString() || "");
    formik.setFieldValue("title", videoData.title || "");
    formik.setFieldValue("description", videoData.description || "");
    embedUrlRef.current = videoData.embedUrl || "";
    formik.setFieldValue("embedUrl", videoData.embedUrl || "");
    formik.setFieldValue(
      "prerequisiteVideos",
      videoData.prerequisiteVideos || []
    );
    formik.setFieldValue("preview", videoData.preview || false);
    formik.setFieldValue(
      "visibility",
      videoData.visibility
        ? { title: "Unhide", value: true }
        : { title: "Hide", value: false }
    );
    const selectedOrder = videoOrders.find(
      (item) => item.orderId === videoData.orderId
    );
    formik.setFieldValue(
      "selectedOrder",
      selectedOrder ? { ...selectedOrder } : {}
    );

    const filteredVideos = videoOrders.filter(
      (item) => item._id !== videoData._id
    );
    setVideoOrderList(filteredVideos);
  }, [videoData, videoOrders]);

  const handleDeleteVideo = async () => {
    await deleteVideo({ videoId: videoData._id, courseId });
    setEditPopup(false);
  };

  const handleEmbedChange = (e) => {
    const newUrl = e.target.value;
    // Always keep ref in sync — this is the definitive source for the URL on submit
    embedUrlRef.current = newUrl;
    formik.setFieldValue("embedUrl", newUrl);
    setDuration(undefined);
  };

  // Save: always read embedUrl from ref (not formik) to bypass async state issues.
  const onSubmitHandler = async () => {
    // Read URL from ref — always the latest typed/pasted value
    const url = (embedUrlRef.current || "").trim();
    if (!url) return;
    if (!isPlayableEmbed(url)) {
      formik.setFieldError("embedUrl", "Enter a valid YouTube, Vimeo, or MP4 link.");
      return;
    }

    const values = formik.values;
    let finalDuration = duration ?? videoData.duration;
    if (typeof finalDuration !== "number" || finalDuration <= 0) finalDuration = 0;
    if (url.toLowerCase().includes("vimeo.com")) {
      const vimeoDur = await getVimeoDuration(url);
      if (vimeoDur != null && vimeoDur > 0) finalDuration = vimeoDur;
    }

    const embedUrlToSave = normalizeEmbedUrlForSave(url);
    const prerequisiteIds = Array.isArray(values.prerequisiteVideos)
      ? values.prerequisiteVideos.map((v) => (v && v._id ? v._id : v)).filter(Boolean)
      : [];

    setSaving(true);
    const data = {
      courseId,
      videoId: videoData._id,
      title: (values.title || "").trim(),
      description: (values.description || "").trim(),
      embedUrl: embedUrlToSave,
      duration: Number(finalDuration) || 0,
      visibility: values.visibility?.value === true || values.visibility?.value === "true",
      orderId: values.orderId,
      preview: values.preview === true || values.preview === "Yes",
      prerequisiteVideos: prerequisiteIds,
    };
    try {
      await editVideo(data);
      setEditPopup(false);
    } finally {
      setSaving(false);
    }
  };

  const saveButtonText = saving ? "Saving video..." : "Save";

  return (
    <form noValidate className={classes.form}>
      <Grid container spacing={8}>
        <Grid item xs={12}>
          <TextFieldGroup
            label="Video Title"
            type="text"
            name="title"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.title}
            errors={
              formik.touched.title && formik.errors.title
                ? formik.errors.title
                : null
            }
          />
          <TextFieldGroup
            label="Video URL (YouTube, Vimeo, or MP4 link)"
            type="text"
            name="embedUrl"
            onChange={handleEmbedChange}
            onBlur={formik.handleBlur}
            value={formik.values.embedUrl}
            errors={
              formik.touched.embedUrl && formik.errors.embedUrl
                ? formik.errors.embedUrl
                : null
            }
          />
          <div style={{ display: "none" }}>
            <ReactPlayer
              url={formik?.values?.embedUrl || null}
              onDuration={(d) => setDuration(d)}
              width="0"
              height="0"
              controls
            />
          </div>
          <TextFieldGroup
            label="Video Description"
            type="text"
            name="description"
            multiline
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.description}
            errors={
              formik.touched.description && formik.errors.description
                ? formik.errors.description
                : null
            }
          />
          <div className={classes.singleSelect}>
            <SelectSingleValues
              placeholder="Free Preview Video"
              label="Free Preview Video"
              name="preview"
              favourite={previewList}
              onSelect={(list) => formik.setFieldValue("preview", list.value)}
              selected={
                formik.values.preview
                  ? { title: "Yes", val: true }
                  : { title: "No", val: false }
              }
            />
          </div>
          <div className={classes.singleSelect}>
            <SelectSingleValues
              placeholder="Hide Video"
              label="Hide Video"
              name="visibility"
              favourite={visibility}
              onSelect={(video) => formik.setFieldValue("visibility", video)}
              selected={formik.values.visibility || {}}
            />
          </div>
          <div className={classes.prerequisite}>
            <SelectMultipleValues
              showLable={true}
              heading="Select prerequisite videos"
              placeholder="Select prerequisite videos"
              name="prerequisiteVideos"
              values={videoOrderList}
              onSelectValues={(video) =>
                formik.setFieldValue("prerequisiteVideos", video)
              }
              selectedValues={formik.values.prerequisiteVideos || []}
            />
          </div>
          <div className={classes.buttonContainer}>
            <ButtonField
              buttonStyle={classes.formSubmit}
              type="button"
              name={saveButtonText}
              onClick={onSubmitHandler}
              disabled={saving}
            />
            <div style={{ marginRight: "15px" }} />
            <ButtonField
              buttonStyle={classes.formSubmit}
              name="Delete Video"
              onClick={() => handleDeleteVideo()}
            />
          </div>
        </Grid>
      </Grid>
    </form>
  );
};

VideoEdit.propTypes = {
  editVideo: PropTypes.func.isRequired,
  deleteVideo: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { editVideo, deleteVideo })(VideoEdit);
